import { Link, NavLink, Outlet } from 'react-router-dom';
import { FeatherLogo } from './FeatherLogo';
import { SITE_NAME } from '../config';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
    isActive ? 'bg-white/15 text-white' : 'text-plume-100 hover:bg-white/10 hover:text-white'
  }`;

/** Site chrome: skip link, glossy header, main landmark, and legal footer. */
export function Layout() {
  return (
    <div className="flex min-h-screen flex-col bg-cream text-plume-950">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-white focus:px-5 focus:py-3 focus:font-semibold focus:text-plume-900 focus:shadow-gloss-lg"
      >
        Skip to main content
      </a>

      <header className="bg-iridescent relative shadow-gloss">
        <div className="bg-sheen pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-plume-300"
          >
            <FeatherLogo className="h-11 w-11 text-tail-300" />
            <span className="font-display text-2xl tracking-wide text-white">
              Peacock <span className="text-gold-300">Omlettes</span>
            </span>
          </Link>
          <nav aria-label="Primary" className="flex items-center gap-1">
            <NavLink to="/recipes" className={navLinkClass}>
              Recipes
            </NavLink>
            <NavLink to="/about" className={navLinkClass}>
              About
            </NavLink>
          </nav>
        </div>
      </header>

      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
        <Outlet />
      </main>

      <footer className="bg-iridescent relative mt-16">
        <div className="bg-sheen pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <div className="flex flex-col gap-8 sm:flex-row sm:justify-between">
            <div className="max-w-sm">
              <div className="flex items-center gap-2">
                <FeatherLogo className="h-8 w-8 text-tail-300" />
                <span className="font-display text-lg text-white">{SITE_NAME}</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-plume-200">
                The web&rsquo;s most trusted peacock omelette resource. Twenty recipes, one
                unwavering editorial standard.
              </p>
            </div>
            <nav aria-label="Legal and privacy">
              <h2 className="text-sm font-semibold uppercase tracking-widest text-gold-300">
                The Fine Print
              </h2>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <Link
                    to="/privacy"
                    className="text-plume-100 underline-offset-2 hover:text-white hover:underline"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-plume-100 underline-offset-2 hover:text-white hover:underline"
                  >
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cookies"
                    className="text-plume-100 underline-offset-2 hover:text-white hover:underline"
                  >
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-settings"
                    className="text-plume-100 underline-offset-2 hover:text-white hover:underline"
                  >
                    Privacy Settings
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-settings"
                    className="text-plume-100 underline-offset-2 hover:text-white hover:underline"
                  >
                    Do Not Sell or Share My Personal Information
                  </Link>
                </li>
                <li>
                  <Link
                    to="/accessibility"
                    className="text-plume-100 underline-offset-2 hover:text-white hover:underline"
                  >
                    Accessibility
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
          <p className="mt-10 border-t border-white/10 pt-6 text-xs leading-relaxed text-plume-300">
            &copy; {new Date().getFullYear()} PeacockOmlettes.com &middot; A humor publication.
            Recipes are presented in earnest technique and questionable premise. Peacocks do not lay
            eggs; peahens do. We are aware. The name tested better.
          </p>
        </div>
      </footer>
    </div>
  );
}
